import { Controller } from "@hotwired/stimulus"

// Click-and-drag horizontal scrolling for a rail, mouse only — touch already
// scrolls natively via the browser, and also handling touch here would fight
// its own momentum scroll. Desktop mice have no other way to move a
// horizontal strip without a trackpad or shift+wheel.
//
// Per javascript.md: the initial pointerdown is wired with data-action;
// transient move/up listeners are added on drag start and removed on release
// (see cover_drag_controller.js for the same pattern).
export default class extends Controller {
  connect() {
    this.element.classList.add("cursor-grab")
  }

  disconnect() {
    this.stopTracking()
  }

  startDrag(event) {
    if (event.pointerType !== "mouse") return

    this.startX = event.clientX
    this.startScrollLeft = this.element.scrollLeft
    this.pointerId = event.pointerId
    this.moved = false

    this.onMove = this.move.bind(this)
    this.onUp = this.stopTracking.bind(this)
    this.element.addEventListener("pointermove", this.onMove)
    this.element.addEventListener("pointerup", this.onUp)
    this.element.addEventListener("pointercancel", this.onUp)
  }

  move(event) {
    const delta = event.clientX - this.startX

    // Only commit to "this is a drag" — and only capture the pointer — once
    // real movement crosses the threshold. Capturing on every pointerdown
    // (including a plain click that never moves) retargets the click event
    // to this element instead of the link/image the user actually pressed,
    // which silently breaks navigation into the recipe or cookbook.
    if (!this.moved && Math.abs(delta) > 5) {
      this.moved = true
      this.element.setPointerCapture(this.pointerId)
      this.element.classList.add("cursor-grabbing")
    }

    if (this.moved) this.element.scrollLeft = this.startScrollLeft - delta
  }

  stopTracking() {
    if (!this.onMove) return
    this.element.removeEventListener("pointermove", this.onMove)
    this.element.removeEventListener("pointerup", this.onUp)
    this.element.removeEventListener("pointercancel", this.onUp)
    this.onMove = null
    this.element.classList.remove("cursor-grabbing")
  }

  // A real drag shouldn't also fire the card link underneath the pointer.
  click(event) {
    if (this.moved) {
      event.preventDefault()
      event.stopPropagation()
      this.moved = false
    }
  }

  // <img> is draggable by default — starting the drag gesture on top of a
  // card's photo would otherwise trigger the browser's native "drag this
  // image out" ghost instead of our own scroll-drag. Suppress it within the
  // rail so dragging from anywhere on a card, including its image, scrolls.
  preventNativeDrag(event) {
    event.preventDefault()
  }
}
